/**
 * Tekst die je direct op het canvas bewerkt.
 *
 * Twee dingen maken dit lastiger dan een `<input>`, en beide zijn hier
 * expliciet opgelost:
 *
 * 1. React mag de inhoud niet opnieuw zetten terwijl de gebruiker in het
 *    veld staat -- dan springt de cursor naar het begin. De DOM-inhoud wordt
 *    daarom alleen gelijkgetrokken als het element géén focus heeft (bij het
 *    laden, na ongedaan maken, of bij het wisselen van pagina).
 * 2. Plakken uit Word of een website mag geen opmaak of HTML meebrengen: het
 *    contentmodel kent alleen platte tekst, dus geplakte inhoud wordt
 *    ontdaan van opmaak.
 */
import React, { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

interface InlineTextProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  multiline: boolean;
  as: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
  className?: string;
  editable: boolean;
  ariaLabel: string;
  onFocus?: () => void;
  onDoubleClick?: () => void;
  onBlur?: () => void;
  style?: React.CSSProperties;
}

export function InlineText({
  value,
  onChange,
  placeholder,
  multiline,
  as,
  className,
  editable,
  ariaLabel,
  onFocus,
  onDoubleClick,
  onBlur,
  style,
}: InlineTextProps) {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (document.activeElement === element) return;
    if (element.textContent === value) return;
    element.textContent = value;
  }, [value]);

  const handleInput = () => {
    const element = ref.current;
    if (!element) return;
    onChange(element.textContent ?? '');
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const text = event.clipboardData.getData('text/plain');
    const normalized = multiline ? text : text.replace(/\s*\n+\s*/g, ' ');
    document.execCommand('insertText', false, normalized);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Delete/Backspace horen tijdens het typen bij de tekst, nooit bij het
    // blok. Het verwijderen van een blok gebeurt via de blokknoppen; deze
    // toetsen mogen daar niet doorheen lekken.
    event.stopPropagation();

    if (event.key === 'Escape') {
      (event.target as HTMLElement).blur();
      return;
    }
    if (event.key === 'Enter' && !multiline) {
      event.preventDefault();
      (event.target as HTMLElement).blur();
    }
  };

  const Tag = as as unknown as React.ElementType;

  return (
    <Tag
      ref={ref as never}
      role="textbox"
      aria-label={ariaLabel}
      aria-multiline={multiline}
      contentEditable={editable}
      suppressContentEditableWarning
      spellCheck
      data-empty={value === '' ? 'true' : 'false'}
      data-placeholder={placeholder}
      className={cn(
        // The published renderer does not add editor-only whitespace or word
        // breaking rules. Keeping this class neutral means a saved newline,
        // long word and line wrap occupy the same pixels in Builder and
        // Preview; editing remains enabled through contentEditable itself.
        'zoea-inline-text',
        as === 'p' && 'zs-rich-text',
        editable && 'cursor-text',
        className,
      )}
      style={style}
      onInput={handleInput}
      onBlur={() => { handleInput(); onBlur?.(); }}
      onPaste={handlePaste}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
      onDoubleClick={() => {
        onDoubleClick?.();
        // The double-click changes the interaction phase and therefore the
        // contentEditable prop. Focus on the next frame so the caret is
        // placed after React has committed that state change.
        if (!editable) requestAnimationFrame(() => ref.current?.focus());
      }}
    />
  );
}
